import { controlTypeFromMinutes } from '@/services/utils/timeControl';
import Player from '@/models/Player';
import Game from '@/models/Game';
import Team from '@/models/Team';
import {
  TournamentStatus,
  GameStage,
  ControlType,
  TournamentSystem,
  ChessVariant,
  CompetitionFormat,
  TeamMode,
  TeamMatchFormat,
  TeamPairingMode,
  RandomTeamFormationMode,
  TournamentCalculateType,
  TournamentParticipantPhase,
  TournamentParticipantStatus,
  TerminationForfeit,
  TerminationWithdrawal,
} from '@/services/utils/enums';
import MatchSide from '@/models/MatchSide';
import TeamMatchBuilder from '@/services/TeamMatchBuilder';
import EliminationService from '@/services/EliminationService';
import { translate } from '@/i18n';

import { makeLogger } from '@/services/utils/logger';
import { LOGGING, VARIANT_RATING_MAP } from '@/config';
const DEBUG_LOG = true;
const log = makeLogger('Tournament', DEBUG_LOG, LOGGING.MODELS_LOG);

function emptyWithdrawnAt() {
  return { phase: null, round: null, cycle: null, stage: null };
}

function normalizePlayerPayments(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  return Object.fromEntries(Object.entries(value).filter(([, payment]) => (
    payment
    && typeof payment === 'object'
    && typeof payment.paid === 'boolean'
    && ['manual', 'subscription', 'manual-free'].includes(payment.mode)
    && !(payment.mode === 'manual-free' && payment.paid)
  )));
}

/**
 * Класс, представляющий шахматный турнир.
 */
export default class Tournament {

  /**
   * Порядок стадий плей-офф.
   * @type {Array<string>}
   */
  static EliminationStageOrder = ['R64', 'R32', 'R16', 'R8', 'QF', 'SF', 'P3', 'F'];

  /**
   * Создает экземпляр турнира.
   * @param {Object} info - Информация о турнире.
   * @param {Object} settings - Настройки турнира.
   * @param {Array<Object|Player>} players - Список игроков.
   * @param {string|string[]|null} clubId - ID клуба или список ID клубов.
   * @param {Object<string,Object>} participants - Нормализованная карта участников турнира.
   */
  constructor(info = {}, settings = {}, players = [], clubId, participants = {}) {
    this.id          = null;//Tournament.#generateId();
    this.clubId      = clubId ? Array.isArray(clubId) ? clubId : [clubId] : [];
    this.name        = info.name        ?? null;
    this.slug        = info.slug        ?? null;
    this.description = info.description ?? null;
    this.place = info.place ? { ...info.place } : null;
    this.clubPlaceId = info.clubPlaceId ?? info.place?.clubPlaceId ?? null;
    this.saveClubPlace = info.saveClubPlace === true;
    this.date        = info.date        ?? null;
    this.time        = info.time        ?? null;
    this.finishedAt  = info.finishedAt  ?? null;
    this.season      = info.season     ?? null;
    this.rated       = info.rated ?? false;
    this.entryFee = info.entryFee ?? null;
    this.currency = info.currency ?? null;
    this.hasEntryFee = info.hasEntryFee ?? false;
    this.acceptsSubscriptions = info.acceptsSubscriptions ?? false;
    this.playerPayments = normalizePlayerPayments(info.playerPayments);
    this.chessVariant = info.chessVariant ?? ChessVariant.CLASSIC;
    this.competitionFormat = info.competitionFormat ?? CompetitionFormat.INDIVIDUAL;
    this.participantLimit = info.participantLimit ?? null;
    this.system = settings.system ?? null;
    this.calculateType = settings.calculateType ?? null;
    this.teamSettings = settings.teamSettings
      ? {
          teamMode: settings.teamSettings.teamMode ?? null,
          teamPairingMode: settings.teamSettings.teamPairingMode ?? null,
          teamMatchFormat: settings.teamSettings.teamMatchFormat ?? null,
          teamSize: settings.teamSettings.teamSize ?? null,
          randomTeamFormationMode: settings.teamSettings.randomTeamFormationMode ?? RandomTeamFormationMode.RANDOM,
        }
      : null;

    // Нормализация настроек по выбранной системе: выбираем источник и один раз формируем объект
    const sys = this.system;

    // 1) Определяем источники настроек (только указатели, без копирования)
    let swSrc = null, elSrc = null, rrSrc = null;
    if (sys === TournamentSystem.MIXED) {
      swSrc = settings.mixed?.swiss ?? null;
      rrSrc = settings.mixed?.roundRobin ?? null;
      elSrc = settings.mixed?.elimination ?? null;
    } else if (sys === TournamentSystem.SWISS) {
      swSrc = settings.swiss ?? null;
    } else if (sys === TournamentSystem.ELIMINATION) {
      elSrc = settings.elimination ?? null;
    } else if (sys === TournamentSystem.ROUND_ROBIN) {
      rrSrc = settings.roundRobin ?? null;
    }

    // 2) Универсальные билдеры (один раз описываем маппинг полей)
    const buildSwiss = (src) => {
      if (!src) return null;
      return {
        rounds              : src.rounds              ?? null,
        timeMinutes         : src.timeMinutes         ?? null,
        increment           : src.increment           ?? null,
        controlType         : src.controlType         ?? null,
        tieBreakCoefficient : src.tieBreakCoefficient ?? null,
        byePoints           : src.byePoints           ?? null
      };
    };

    const buildElimination = (src) => {
      if (!src) return null;
      return {
        bracketsType      : src.bracketsType       ?? null,
        timeMinutes       : src.timeMinutes        ?? null,
        increment         : src.increment          ?? null,
        controlType       : src.controlType        ?? null,
        winCondition      : src.winCondition       ?? null,
        countToWin        : src.countToWin         ?? null,
        tieBreaker        : src.tieBreaker         ?? null,
        participantsMode  : src.participantsMode   ?? null,
        participantsTop   : src.participantsTop    ?? null,
        participantsBottom: src.participantsBottom ?? null,
        colorRule         : src.colorRule          ?? null,
        teamScoringUnit   : src.teamScoringUnit    ?? null,
      };
    };

    const buildRoundRobin = (src) => {
      if (!src) return null;
      return {
        cycles              : src.cycles              ?? null,
        timeMinutes         : src.timeMinutes         ?? null,
        increment           : src.increment           ?? null,
        controlType         : src.controlType         ?? null,
        tieBreakCoefficient : src.tieBreakCoefficient ?? null,
        randomizeLastOddCycleColors: src.randomizeLastOddCycleColors ?? true,
      };
    };

    // 3) Строим нормализованные объекты (по факту источников)
    const swiss       = buildSwiss(swSrc);
    const elimination = buildElimination(elSrc);
    const roundRobin  = buildRoundRobin(rrSrc);

    // 4) Назначаем только поля выбранной системы
    if (sys === TournamentSystem.MIXED) {
      this.mixed = {
        ...(swiss ? { swiss } : {}),
        ...(roundRobin ? { roundRobin } : {}),
        elimination: elimination ?? {},
      };
    } else if (sys === TournamentSystem.SWISS) {
      if (swiss) this.swiss = swiss;
    } else if (sys === TournamentSystem.ELIMINATION) {
      if (elimination) this.elimination = elimination;
    } else if (sys === TournamentSystem.ROUND_ROBIN) {
      if (roundRobin) this.roundRobin = roundRobin;
    }

    this.players = players.map(
      p => (p instanceof Player ? p : new Player(p.name, this.clubId))
    );
    this.teams = (info.teams || []).map(
      (team) => (team instanceof Team ? team : Team.fromObject(team))
    );
    this.participants = (participants && typeof participants === 'object' && !Array.isArray(participants))
      ? participants
      : {};
    this.roundRosters = {};
    this.showChess960Generator = info.showChess960Generator === true;
    this.pairingEngine = null;
    /**
     * Runtime-карта рейтинговых итогов физических игроков.
     * Заполняется после загрузки турнира из рейтингового preview или cache:
     * {
     *   [playerId]: {
     *     [controlType]: { start, final, delta, performance, gamesCount, provisional }
     *   }
     * }
     *
     * Это не persisted-поле Tournament. Турнирные показатели считаются по participant,
     * а рейтинги, дельты и перформанс читаются по физическому playerId.
     */
    this.playerTotals = info.playerTotals ?? null;
    this.games = {
      regular: [],
      elimination: {
        top: [],
        bottom: []
      },
      other: []
    };
    this.stage = null;
    this.status  = TournamentStatus.CREATED;
    this.createdAt = info.createdAt ?? new Date().toISOString();
    this.updatedAt = info.updatedAt ?? new Date().toISOString();
    this.cache      = info.cache ?? null;
    this.nominations = info.nominations ?? [];
    this.nominationSnapshots = info.nominationSnapshots ?? {};
  }

  /**
   * Создает экземпляр турнира из plain-объекта.
   * @param {Object} obj - Plain-объект турнира.
   * @returns {Tournament}
   */
  static fromObject(obj) {
    const tournament = new Tournament(
      {
        name: obj.name,
        slug: obj.slug,
        description: obj.description,
        date: obj.date,
        time: obj.time,
        finishedAt: obj.finishedAt,
        season: obj.season,
        chessVariant: obj.chessVariant,
        rated: obj.rated,
        entryFee: obj.entryFee,
        currency: obj.currency,
        hasEntryFee: obj.hasEntryFee,
        acceptsSubscriptions: obj.acceptsSubscriptions,
        playerPayments: obj.playerPayments,
        participantLimit: obj.participantLimit ?? null,
      },
      {
        system: obj.system,
        calculateType: obj.calculateType,
        mixed: obj.mixed,
        swiss: obj.swiss,
        elimination: obj.elimination,
        roundRobin: obj.roundRobin,
        teamSettings: obj.teamSettings,
      },
      obj.players?.map(p => Player.fromObject(p)) || [],
      obj.clubId,
      obj.participants || {}
    );
    log('fromObject', '📥 Исходный объект:', obj);
    tournament.id = obj.id || null;//Tournament.#generateId();
    tournament.status = obj.status || TournamentStatus.CREATED;
    tournament.stage = obj.stage || 'regular';
    tournament.createdAt = obj.createdAt ?? new Date().toISOString();
    tournament.updatedAt = obj.updatedAt ?? new Date().toISOString();
    tournament.finishedAt = obj.finishedAt ?? null;
    tournament.season  = obj.season ?? null;
    tournament.competitionFormat = obj.competitionFormat ?? CompetitionFormat.INDIVIDUAL;
    tournament.teamSettings = obj.teamSettings
      ? {
          ...obj.teamSettings,
          randomTeamFormationMode: obj.teamSettings.randomTeamFormationMode ?? RandomTeamFormationMode.RANDOM,
        }
      : null;
    tournament.teams = (obj.teams || []).map((team) => Team.fromObject(team));
    tournament._debugLogs = obj._debugLogs ?? [];
    tournament.cache = obj.cache ?? null;
    tournament.roundRosters = JSON.parse(JSON.stringify(obj.roundRosters ?? {}));
    tournament.showChess960Generator = obj.showChess960Generator === true;
    tournament.pairingEngine = obj.pairingEngine ?? null;
    tournament.nominations = obj.nominations ?? [];
    tournament.place = obj.place ? { ...obj.place } : null;
    tournament.clubPlaceId = obj.clubPlaceId ?? obj.place?.clubPlaceId ?? null;
    tournament.saveClubPlace = obj.saveClubPlace === true;
    tournament.nominationSnapshots = obj.nominationSnapshots ?? {};
    tournament.games = {
      regular: (obj.games?.regular || []).map(m => Game.fromObject(m)),
      elimination: {
        top: (obj.games?.elimination?.top || []).map(m => Game.fromObject(m)),
        bottom: (obj.games?.elimination?.bottom || []).map(m => Game.fromObject(m))
      },
      other: (obj.games?.other || []).map(m => Game.fromObject(m))
    };
    log('fromObject', '✅ После создания матчей:', tournament.games);
    log('fromObject', '✅ Готовый объект турнира:', tournament);
    return tournament;
  }

  /**
   * Доменный контракт participants.
   *
   * TournamentService отвечает за преобразования API:
   * - сырой JSON из Strapi -> нормализованные participant-записи;
   * - participant-записи -> JSON без runtime-полей перед сохранением.
   *
   * Модель Tournament не знает о формате API. Она работает с уже подготовленной
   * картой участников и изменяет только доменные поля.
   *
 * Persisted JSON:
 * {
 *   [participantId]: {
 *     type: 'player' | 'team',
 *     ownerTeamId: 'fixed-team-id-or-null',
 *     ownerEntityId: 'original-player-id-or-null',
 *     currentEntityId: 'current-player-or-team-id',
   *     status: 'active' | 'withdrawn' | 'replaced' | 'substituted',
   *     withdrawnAt: { phase, round, cycle, stage },
   *     replacements: [{ fromEntityId, toEntityId, replacedAt, createdAt }],
   *     updatedAt,
   *   }
   * }
   *
   * Runtime accessors add:
   * - id: participantId из ключа;
   * - entity: текущий Player/Team;
   * - replacements[].entity: Player/Team замененного участника.
   */
  /**
   * Возвращает participant-запись по ID и добавляет runtime-ссылки на сущности.
   * Метод не создает отсутствующих участников и не нормализует persisted-формат.
   * @param {string|null} participantId - ID participant-слота.
   * @returns {Object|null}
   */
  getParticipant(participantId) {
    if (!participantId) return null;

    const raw = this.participants?.[participantId] ?? null;
    if (!raw) return null;

    const type = raw.type;
    const currentEntityId = raw.currentEntityId;
    const entity = type === 'team' ? this.getTeamById(currentEntityId) : this.getPlayerById(currentEntityId);

    return {
      ...raw,
      id: participantId,
      entity,
      replacements: (raw?.replacements || []).map((replacement) => {
        const replacementId = replacement?.fromEntityId ?? null;
        return {
          ...replacement,
          entity: type === 'team' ? this.getTeamById(replacementId) : this.getPlayerById(replacementId),
        };
      }).filter((replacement) => replacement.fromEntityId),
    };
  }

  /**
   * Находит игрока турнира по ID.
   * Дополнительная проверка по имени нужна для старых локальных объектов без ID.
   * @param {string|null} playerId
   * @returns {Player|null}
   */
  getPlayerById(playerId) {
    if (!playerId || !Array.isArray(this.players)) return null;
    return this.players.find((player) =>
      player?.id === playerId || (!player?.id && player?.name === playerId)
    ) ?? null;
  }

  /**
   * Находит fixed-команду турнира по ID.
   * @param {string|null} teamId
   * @returns {Team|null}
   */
  getTeamById(teamId) {
    if (!teamId || !Array.isArray(this.teams)) return null;
    return this.teams.find((team) => team?.id === teamId) ?? null;
  }

  /**
   * Возвращает список participant-записей из карты `participants`.
   * Метод не достраивает участников из `players` или `teams`.
   * @param {Object} [options={}]
   * @param {'player'|'team'|null} [options.type=null] - Необязательный фильтр по типу участника.
   * @returns {Object[]}
   */
  getParticipants({ type = null } = {}) {
    return Object.entries(this.participants || {})
      .map(([participantId]) => this.getParticipant(participantId))
      .filter(Boolean)
      .filter((participant) => !type || participant.type === type);
  }

  /**
   * Возвращает первый participant, в котором указанная сущность участвует сейчас или была заменена.
   * Используется для задач, где нужен любой связанный participant, а не только активный текущий слот.
   * @param {string|null} entityId - ID игрока или команды.
   * @returns {Object|null}
   */
  getParticipantByEntityId(entityId) {
    if (!entityId) return null;
    return this.getParticipants().find((participant) =>
      this.getParticipantEntityIds(participant.id).includes(entityId)
    ) ?? null;
  }

  /**
   * Возвращает активный player-participant, где указанная сущность является текущим игроком.
   * Не ищет по истории замен, только по `currentEntityId`.
   * @param {string|null} entityId - ID текущего игрока.
   * @returns {Object|null}
   */
  getActiveParticipantByCurrentEntityId(entityId) {
    if (!entityId) return null;
    return this.getParticipants({ type: 'player' }).find((participant) =>
      participant.currentEntityId === entityId && this.isParticipantActive(participant.id)
    ) ?? null;
  }

  /**
   * Возвращает ID всех физических сущностей, связанных с participant-слотом.
   * Первым идет текущий `currentEntityId`, затем игроки/команды из `replacements[].fromEntityId`.
   * @param {string|null} participantId
   * @returns {string[]}
   */
  getParticipantEntityIds(participantId) {
    const participant = this.getParticipant(participantId);
    const ids = [
      participant?.currentEntityId,
      ...(Array.isArray(participant?.replacements) ? participant.replacements.map((replacement) => replacement?.fromEntityId) : []),
    ];

    return Array.from(new Set(ids.filter(Boolean)));
  }

  /**
   * Возвращает объекты Player/Team, связанные с participant-слотом.
   * @param {Object|null} participant
   * @returns {Array<Player|Team>}
   */
  getParticipantEntities(participant) {
    return this.getParticipantEntityIds(participant?.id)
      .map((entityId) => participant?.type === 'team' ? this.getTeamById(entityId) : this.getPlayerById(entityId))
      .filter(Boolean);
  }

  /**
   * Проверяет, активен ли участник турнира.
   * @param {string|null} participantId
   * @returns {boolean}
   */
  isParticipantActive(participantId) {
    const status = this.participants?.[participantId]?.status ?? null;
    if (!status) return false;

    return status !== TournamentParticipantStatus.WITHDRAWN
      && status !== TournamentParticipantStatus.SUBSTITUTED;
  }

  /**
   * Проверяет, может ли participant-слот прямо сейчас участвовать в игре.
   * Для fixed-team player-слота учитывает не только статус самого игрока,
   * но и статус команды, которой этот слот принадлежит.
   * @param {string|null} participantId
   * @returns {boolean}
   */
  isParticipantEffectivelyActive(participantId) {
    if (!this.isParticipantActive(participantId)) return false;

    const participant = this.getParticipant(participantId);
    if (
      participant?.type === 'player'
      && this.isTeamTournament()
      && this.teamSettings?.teamMode === TeamMode.FIXED
    ) {
      const team = this.getFixedTeamForParticipant(participant);
      return team?.id ? this.isParticipantActive(team.id) : true;
    }

    return true;
  }

  /**
   * Возвращает статус participant после ручного возврата в турнир.
   * Если у слота есть история замен, он возвращается как replaced, иначе active.
   * @param {Object|null} participant
   * @returns {string}
   */
  getParticipantStatusAfterManualRestore(participant) {
    const hasReplacements = Array.isArray(participant?.replacements) && participant.replacements.length > 0;
    return hasReplacements ? TournamentParticipantStatus.REPLACED : TournamentParticipantStatus.ACTIVE;
  }

  /**
   * Возвращает статус participant после изменения currentEntityId через замену.
   * Снятый или substituted слот остается неактивным как withdrawn; активные слоты становятся replaced.
   * @param {Object|null} participant
   * @returns {string}
   */
  getParticipantStatusAfterReplacement(participant) {
    if (
      participant?.status === TournamentParticipantStatus.WITHDRAWN
      || participant?.status === TournamentParticipantStatus.SUBSTITUTED
    ) {
      return TournamentParticipantStatus.WITHDRAWN;
    }

    return TournamentParticipantStatus.REPLACED;
  }

  /**
   * Возвращает withdrawnAt после изменения currentEntityId через замену.
   * Для неактивных слотов сохраняет/создает момент снятия, для активных очищает.
   * @param {Object|null} participant
   * @param {Object|null} replacedAt
   * @returns {{phase: string|null, round: number|null, cycle: number|null, stage: string|null}}
   */
  getWithdrawnAtAfterReplacement(participant, replacedAt = {}) {
    if (participant?.status === TournamentParticipantStatus.WITHDRAWN) {
      return participant.withdrawnAt || emptyWithdrawnAt();
    }

    if (participant?.status === TournamentParticipantStatus.SUBSTITUTED) {
      return {
        phase: replacedAt?.phase ?? TournamentParticipantPhase.REGULAR,
        round: replacedAt?.round ?? null,
        cycle: replacedAt?.cycle ?? null,
        stage: replacedAt?.stage ?? null,
      };
    }

    return emptyWithdrawnAt();
  }

  /**
   * Помечает участника снятым с турнира.
   * @param {string} participantId
   * @param {{phase?: string|null, round?: number|null, cycle?: number|null, stage?: string|null}} [withdrawnAt={}]
   * @returns {void}
   */
  withdrawParticipant(participantId, withdrawnAt = {}) {
    if (!participantId) return;
    const current = this.participants?.[participantId] ?? null;
    if (!current) return;

    this.participants = {
      ...(this.participants || {}),
      [participantId]: {
        ...current,
        status: TournamentParticipantStatus.WITHDRAWN,
        withdrawnAt: {
          phase: withdrawnAt?.phase ?? TournamentParticipantPhase.REGULAR,
          round: withdrawnAt?.round ?? null,
          cycle: withdrawnAt?.cycle ?? null,
          stage: withdrawnAt?.stage ?? null,
        },
        updatedAt: new Date().toISOString(),
      }
    };
  }

  /**
   * Возвращает участника в активное состояние.
   * @param {string} participantId
   * @returns {void}
   */
  restoreParticipant(participantId) {
    if (!participantId) return;
    const current = this.participants?.[participantId] ?? null;
    if (!current) return;

    this.participants = {
      ...(this.participants || {}),
      [participantId]: {
        ...current,
        status: this.getParticipantStatusAfterManualRestore(current),
        withdrawnAt: emptyWithdrawnAt(),
        updatedAt: new Date().toISOString(),
      }
    };
  }

  /**
   * Помечает собственный participant игрока как временно занятый заменой в другом слоте.
   * Используется, когда игрок уже есть в турнире и выходит на замену за другого participant.
   * @param {string} participantId
   * @param {{phase?: string|null, round?: number|null, cycle?: number|null, stage?: string|null}} [substitutedAt={}]
   * @returns {void}
   */
  substituteParticipant(participantId, substitutedAt = {}) {
    if (!participantId) return;
    const current = this.participants?.[participantId] ?? null;
    if (!current) return;

    this.participants = {
      ...(this.participants || {}),
      [participantId]: {
        ...current,
        status: TournamentParticipantStatus.SUBSTITUTED,
        withdrawnAt: {
          phase: substitutedAt?.phase ?? TournamentParticipantPhase.REGULAR,
          round: substitutedAt?.round ?? null,
          cycle: substitutedAt?.cycle ?? null,
          stage: substitutedAt?.stage ?? null,
        },
        updatedAt: new Date().toISOString(),
      }
    };
  }

  /**
   * Заменяет текущую сущность participant-слота на другого игрока.
   * Старый текущий игрок записывается в `replacements` как `fromEntityId`.
   * @param {string} participantId - ID participant-слота, в котором происходит замена.
   * @param {string} nextPlayerId - ID игрока, который становится текущим.
   * @param {{phase?: string|null, round?: number|null, cycle?: number|null, stage?: string|null}} [replacedAt={}]
   * @returns {void}
   */
  replaceParticipant(participantId, nextPlayerId, replacedAt = {}) {
    if (!participantId || !nextPlayerId || participantId === nextPlayerId) return;
    const current = this.participants?.[participantId] ?? null;
    if (!current) return;
    const now = new Date().toISOString();
    const currentEntityId = current.currentEntityId;
    if (!currentEntityId) return;
    if (currentEntityId === nextPlayerId) return;
    const outgoingReplacement = {
      fromEntityId: currentEntityId,
      toEntityId: nextPlayerId,
      replacedAt: {
        phase: replacedAt?.phase ?? TournamentParticipantPhase.REGULAR,
        round: replacedAt?.round ?? null,
        cycle: replacedAt?.cycle ?? null,
        stage: replacedAt?.stage ?? null,
      },
      createdAt: now,
    };

    const persistedReplacements = Array.isArray(current.replacements) ? current.replacements : [];
    const nextStatus = this.getParticipantStatusAfterReplacement(current);

    this.participants = {
      ...(this.participants || {}),
      [participantId]: {
        ...current,
        currentEntityId: nextPlayerId,
        status: nextStatus,
        withdrawnAt: this.getWithdrawnAtAfterReplacement(current, replacedAt),
        replacements: [
          ...persistedReplacements,
          outgoingReplacement,
        ],
        updatedAt: now,
      },
    };
  }

  /**
   * Возвращает ранее замененного игрока в качестве текущей сущности participant-слота.
   * Восстанавливаемый игрок удаляется из `replacements`, а уходящий текущий игрок добавляется туда при необходимости.
   * @param {string} participantId - ID participant-слота.
   * @param {string} restoredPlayerId - ID игрока, которого нужно сделать текущим.
   * @param {{phase?: string|null, round?: number|null, cycle?: number|null, stage?: string|null}} [replacedAt={}]
   * @returns {void}
   */
  restoreReplacementParticipant(participantId, restoredPlayerId, replacedAt = {}) {
    if (!participantId || !restoredPlayerId) return;
    const current = this.participants?.[participantId] ?? null;
    if (!current) return;
    const now = new Date().toISOString();
    const currentEntityId = current.currentEntityId;
    if (!currentEntityId) return;
    if (currentEntityId === restoredPlayerId) return;

    const persistedReplacements = (Array.isArray(current.replacements) ? current.replacements : [])
      .filter((replacement) => replacement.fromEntityId !== restoredPlayerId);

    const alreadyHasOutgoing = persistedReplacements.some((replacement) => (
      replacement.fromEntityId === currentEntityId
    ));

    const outgoingReplacement = alreadyHasOutgoing ? null : {
      fromEntityId: currentEntityId,
      toEntityId: restoredPlayerId,
      replacedAt: {
        phase: replacedAt?.phase ?? TournamentParticipantPhase.REGULAR,
        round: replacedAt?.round ?? null,
        cycle: replacedAt?.cycle ?? null,
        stage: replacedAt?.stage ?? null,
      },
      createdAt: now,
    };
    const nextReplacements = outgoingReplacement
      ? [...persistedReplacements, outgoingReplacement]
      : persistedReplacements;
    const nextStatus = this.getParticipantStatusAfterReplacement(current);

    this.participants = {
      ...(this.participants || {}),
      [participantId]: {
        ...current,
        currentEntityId: restoredPlayerId,
        status: nextStatus === TournamentParticipantStatus.WITHDRAWN
          ? TournamentParticipantStatus.WITHDRAWN
          : nextReplacements.length
            ? TournamentParticipantStatus.REPLACED
            : TournamentParticipantStatus.ACTIVE,
        withdrawnAt: this.getWithdrawnAtAfterReplacement(current, replacedAt),
        replacements: nextReplacements,
        updatedAt: now,
      },
    };
  }

  /**
   * Возвращает все команды турнира, в состав которых входит игрок.
   * При заменах игрок может временно числиться в нескольких командах.
   * @param {string|null} playerId
   * @returns {Team[]}
   */
  getPlayerTeams(playerId) {
    if (!playerId || !Array.isArray(this.teams)) return [];

    return this.teams.filter((team) =>
      Array.isArray(team?.players) && team.players.some((player) => player?.id === playerId)
    );
  }

  /**
   * Находит fixed-команду, к которой относится player-participant слот.
   * Принадлежность слота к команде берется только из persisted `ownerTeamId`.
   * Legacy-восстановление ownerTeamId выполняет TournamentService при десериализации.
   * @param {Object|string|null} participantOrId
   * @returns {Team|null}
   */
  getFixedTeamForParticipant(participantOrId) {
    const participant = typeof participantOrId === 'string'
      ? this.getParticipant(participantOrId)
      : participantOrId;
    return this.getTeamById(participant?.ownerTeamId ?? null);
  }

  /**
   * Возвращает текущих активных игроков fixed-команды через player-participants.
   * `team.players` остается базовым/историческим составом, а текущий состав берется из слотов.
   * @param {Team|Object|null} team
   * @returns {Player[]}
   */
  getFixedTeamCurrentPlayers(team) {
    if (!team?.id) return [];

    const activeTeam = this.isParticipantActive(team.id);
    if (!activeTeam) return [];

    return this.getParticipants({ type: 'player' })
      .filter((participant) => this.isParticipantActive(participant.id))
      .filter((participant) => this.getFixedTeamForParticipant(participant)?.id === team.id)
      .map((participant) => participant.entity)
      .filter(Boolean);
  }

  /**
   * Возвращает игроков fixed-команды для отображения состава.
   * Замененный слот показывает текущего игрока, снятый слот остается в списке для w/d.
   * @param {Team|Object|null} team
   * @returns {Player[]}
   */
  getFixedTeamDisplayPlayers(team) {
    if (!team?.id) return [];

    return this.getParticipants({ type: 'player' })
      .filter((participant) => this.getFixedTeamForParticipant(participant)?.id === team.id)
      .map((participant) => participant.entity)
      .filter(Boolean);
  }

  /**
   * Находит команду турнира, к которой игрок относится в текущем активном контексте.
   * Если игрок есть в нескольких командах из-за замены, выбирает команду исходного
   * игрока из активного participant-слота.
   * @param {string|null} playerId
   * @returns {Team|null}
   */
  getPlayerTeam(playerId) {
    if (this.isTeamTournament() && this.teamSettings?.teamMode === TeamMode.FIXED) {
      const activeParticipant = this.getActiveParticipantByCurrentEntityId(playerId);
      const activeTeam = this.getFixedTeamForParticipant(activeParticipant);
      if (activeTeam) return activeTeam;
    }

    const teams = this.getPlayerTeams(playerId);
    if (teams.length <= 1) return teams[0] ?? null;

    const participant = this.getActiveParticipantByCurrentEntityId(playerId);
    const replacedEntityIds = new Set(
      (participant?.replacements || [])
        .map((replacement) => replacement?.fromEntityId)
        .filter(Boolean)
    );

    const activeReplacementTeam = teams.find((team) => (
      Array.isArray(team?.players)
      && team.players.some((player) => replacedEntityIds.has(player?.id))
    ));
    if (activeReplacementTeam) return activeReplacementTeam;

    const activeTeam = teams.find((team) => this.isParticipantActive(team?.id));
    return activeTeam ?? teams[0] ?? null;
  }

  /**
   * Возвращает игроков команды, доступных для жеребьёвки.
   * Учитывает как снятие самого игрока, так и снятие его команды.
   * @param {Team|Object|null} team
   * @returns {Player[]}
   */
  getActiveTeamPlayers(team) {
    if (this.isTeamTournament() && this.teamSettings?.teamMode === TeamMode.FIXED) {
      return this.getFixedTeamCurrentPlayers(team);
    }

    const members = Array.isArray(team?.players) ? team.players : [];
    return members.filter((player) => this.isPlayerEffectivelyActive(player?.id));
  }

  /**
   * Возвращает игроков команды для UI-отображения состава.
   * Для fixed-team использует participant-слоты, чтобы показывать замены и снятия.
   * @param {Team|Object|null} team
   * @returns {Player[]}
   */
  getTeamDisplayPlayers(team) {
    if (this.isTeamTournament() && this.teamSettings?.teamMode === TeamMode.FIXED) {
      return this.getFixedTeamDisplayPlayers(team);
    }

    return Array.isArray(team?.players) ? team.players.filter(Boolean) : [];
  }

  /**
   * Проверяет фактическую активность игрока.
   * В командном турнире игрок считается снятым, если снята его команда.
   * @param {string|null} playerId
   * @returns {boolean}
   */
  isPlayerEffectivelyActive(playerId) {
    if (!playerId) return false;

    const participant = this.getActiveParticipantByCurrentEntityId(playerId);
    if (!participant) return false;

    if (!this.isTeamTournament()) return true;

    const team = this.getPlayerTeam(playerId);
    if (!team?.id) return true;

    return this.isParticipantActive(team.id);
  }

  /**
   * Возвращает активных игроков турнира с учётом командного снятия.
   * @returns {Player[]}
   */
  getActivePlayers() {
    return this.getParticipants({ type: 'player' })
      .filter((participant) => this.isParticipantActive(participant.id))
      .map((participant) => participant.entity)
      .filter(Boolean)
      .filter((player) => this.isPlayerEffectivelyActive(player.id));
  }

  /**
   * Возвращает рейтинговую информацию участника текущего типа турнира.
   * Для fixed-team это средний рейтинг команды, иначе рейтинг игрока.
   * @param {Player|Team|Object|null} participant
   * @returns {Object}
   */
  getParticipantRatingInfo(participant) {
    const controlType = this.getControlType();

    if (this.isTeamTournament() && this.teamSettings?.teamMode === TeamMode.FIXED) {
      return participant?.getRatingInfo?.(controlType) ?? {
        controlType,
        rating: null,
        memberCount: 0,
        source: 'team-average',
      };
    }

    return participant?.getRatingInfo?.(controlType) ?? {
      controlType,
      rating: null,
      provisional: false,
      source: 'player',
    };
  }

  /**
   * Возвращает активных игроков как универсальные стороны матчей.
   * @returns {MatchSide[]}
   */
  getActivePlayersSides() {
    const playerParticipants = this.getParticipants({ type: 'player' });
    const activeParticipants = playerParticipants.filter((participant) => (
      this.isParticipantActive(participant.id)
    ));
    const participantEntities = activeParticipants.map((participant) => ({
      participant,
      entity: participant.entity,
    }));
    const validParticipantEntities = participantEntities.filter(({ entity }) => entity);
    const effectivelyActiveParticipantEntities = validParticipantEntities.filter(({ entity }) => (
      this.isPlayerEffectivelyActive(entity.id)
    ));

    return effectivelyActiveParticipantEntities.map(({ participant, entity }) => MatchSide.fromObject({
      entityType: 'player',
      id: participant.id,
      name: entity.name,
      members: [entity],
      ratingInfo: this.getParticipantRatingInfo(entity),
      meta: {
        participantId: participant.id,
        replacements: participant.replacements ?? [],
      },
      entity,
    }));
  }

  /**
   * Возвращает активные fixed-команды как универсальные стороны матчей.
   * @returns {MatchSide[]}
   */
  getActiveTeams() {
    if (this.teamSettings?.teamMode !== TeamMode.FIXED) {
      throw new Error('Team participants are implemented only for fixed teams');
    }

    return (this.teams || [])
      .filter((team) => this.isParticipantActive(team?.id))
      .map((team) => MatchSide.fromObject({
        entityType: 'team',
        id: team?.id ?? null,
        name: team?.name ?? '',
        members: this.getActiveTeamPlayers(team),
        ratingInfo: this.getParticipantRatingInfo(team),
        entity: team,
      }));
  }

  /**
   * Возвращает активные стороны матчей в зависимости от формата.
   * Individual/random-team возвращают player MatchSide, fixed-team возвращает team MatchSide.
   * @returns {MatchSide[]}
   */
  getActiveMatchSides() {
    if (this.isTeamTournament() && this.teamSettings?.teamMode === TeamMode.FIXED) {
      return this.getActiveTeams();
    }

    return this.getActivePlayersSides();
  }

  /**
   * Обновляет временную метку последнего изменения турнира.
   */
  updateTimestamp() {
    this.updatedAt = new Date().toISOString();
  }

  /**
   * Возвращает настройки швейцарской стадии для текущей системы турнира.
   * В mixed-турнире берет настройки из `mixed.swiss`.
   * @returns {Object|null}
   */
  getSwissSettings() {
    if (this.system === TournamentSystem.MIXED && this.getRegularSystem() === TournamentSystem.SWISS) return this.mixed?.swiss || null;
    if (this.system === TournamentSystem.SWISS) return this.swiss || null;
    return null;
  }

  /**
   * Возвращает настройки плей-офф для текущей системы турнира.
   * В mixed-турнире берет настройки из `mixed.elimination`.
   * @returns {Object|null}
   */
  getEliminationSettings() {
    if (this.system === TournamentSystem.MIXED) return this.mixed?.elimination || null;
    if (this.system === TournamentSystem.ELIMINATION) return this.elimination || null;
    return null;
  }

  /**
   * Возвращает настройки круговой системы для round-robin турнира.
   * @returns {Object|null}
   */
  getRoundRobinSettings() {
    if (this.system === TournamentSystem.MIXED && this.getRegularSystem() === TournamentSystem.ROUND_ROBIN) return this.mixed?.roundRobin || null;
    if (this.system === TournamentSystem.ROUND_ROBIN) return this.roundRobin || null;
    return null;
  }

  /**
   * Возвращает систему регулярной стадии. У mixed она хранится отдельно от
   * плей-офф, у остальных турниров совпадает с основной системой.
   * @returns {string|null}
   */
  getRegularSystem() {
    if (this.system !== TournamentSystem.MIXED) return this.system;
    return this.mixed?.roundRobin ? TournamentSystem.ROUND_ROBIN : TournamentSystem.SWISS;
  }

  /**
   * Проверяет, является ли турнир командным.
   * @returns {boolean}
   */
  isTeamTournament() {
    return this.competitionFormat === CompetitionFormat.TEAM;
  }

  /**
   * Возвращает настройки командного турнира.
   * @returns {Object|null}
   */
  getTeamSettings() {
    return this.teamSettings || null;
  }

  /**
   * Возвращает, нужно ли учитывать цвета как цвета сторон при расчётах истории.
   * Для командных режимов с досками стороны команды не равны цветам игроков.
   * @returns {boolean}
   */
  shouldUsePairingColors() {
    if (!this.isTeamTournament()) {
      return true;
    }

    return this.teamSettings?.teamMatchFormat === TeamMatchFormat.COMMON_BOARD;
  }

  /**
   * Возвращает размер команды из настроек турнира.
   * @returns {number|null}
   */
  getTeamSize() {
    const savedSize = Number(this.teamSettings?.teamSize);
    if (Number.isFinite(savedSize) && savedSize >= 2) {
      return savedSize;
    }
    return null;
  }

  /**
   * Собирает фронтовые TeamMatch из партий турнира.
   *
   * Источник истины остаётся на уровне Game.
   * Этот метод только агрегирует партии с одинаковым `teamMatchKey`
   * в более удобную для командного UI и расчётов структуру.
   *
   * @param {Game[]|null} games - Необязательный список партий. Если не передан, используются все игры турнира.
   * @returns {import('@/models/TeamMatch').default[]}
   */
  getTeamMatches(games = null) {
    const sourceGames = Array.isArray(games) ? games : this.getAllGames();
    const teamMatchFormat = this.teamSettings?.teamMatchFormat ?? null;

    return TeamMatchBuilder.build(sourceGames, {
      teamMatchFormat,
      teams: this.teams || [],
      players: this.players || [],
    });
  }

  /**
   * Возвращает код тай-брейка для активной системы турнира.
   * Для mixed используется swiss-часть регулярного этапа.
   * @returns {string|null}
   */
  getTieBreakCoefficient() {
    if (this.getRegularSystem() === TournamentSystem.ROUND_ROBIN) {
      return this.getRoundRobinSettings()?.tieBreakCoefficient ?? null;
    }

    if (this.getRegularSystem() === TournamentSystem.SWISS) {
      return this.getSwissSettings()?.tieBreakCoefficient ?? null;
    }

    return null;
  }

  /**
   * Определяет тип контроля времени по количеству минут.
   * @param {number|string} minutes
   * @returns {ControlType|null}
   */
  static controlTypeFromMinutes(minutes) {
    return controlTypeFromMinutes(minutes);
  }

  /**
   * Возвращает тип рейтинга по выбранной разновидности шахмат.
   * @returns {string}
   */
  getRatingType() {
    return VARIANT_RATING_MAP[this.chessVariant] || VARIANT_RATING_MAP[ChessVariant.CLASSIC];
  }

  /**
   * Возвращает тип контроля времени в зависимости от текущей системы турнира.
   * @returns {ControlType|null}
   */
  getControlType() {
    let minutes = null;
    let controlType = null;

    if (this.getRegularSystem() === TournamentSystem.SWISS) {
      const settings = this.getSwissSettings();
      controlType = settings?.controlType ?? null;
      minutes = settings?.timeMinutes ?? null;
    } else if (this.getRegularSystem() === TournamentSystem.ROUND_ROBIN) {
      const settings = this.getRoundRobinSettings();
      controlType = settings?.controlType ?? null;
      minutes = settings?.timeMinutes ?? null;
    }

    return controlType || Tournament.controlTypeFromMinutes(minutes);
  }

  /**
   * Возвращает тип контроля времени для плей-офф.
   * @returns {ControlType|null}
   */
  getControlTypeForElimination() {
    const settings = this.getEliminationSettings();
    return settings?.controlType || Tournament.controlTypeFromMinutes(settings?.timeMinutes) || this.getControlType();
  }

  /**
   * Переводит турнир в статус "В процессе".
   */
  start() {
    this.status = TournamentStatus.ONGOING;
  }

  /**
   * Завершает турнир.
   */
  finish() {
    this.status = TournamentStatus.FINISHED;
  }

  /**
   * Добавляет игрока в физический список игроков турнира.
   * Если передан готовый объект игрока, добавляет его без дублирования по id.
   * Если передана строка, создаёт нового игрока с этим именем.
   * @param {Player|Object|string} player
   * @returns {Player|Object|null}
   */
  addPlayer(player) {
    if (!Array.isArray(this.players)) {
      this.players = [];
    }

    if (player && typeof player === 'object') {
      if (player.id && this.players.some((item) => item?.id === player.id)) {
        return player;
      }

      this.players.push(player);
      return player;
    }

    if (typeof player !== 'string' || !player.trim()) return null;

    const newPlayer = new Player(player.trim(), this.clubId);
    this.players.push(newPlayer);
    return newPlayer;
  }

  /**
   * Удаляет игрока из турнира по его ID.
   * @param {string} playerId
   */
  removePlayer(playerId) {
    this.players = this.players.filter(player => player.id !== playerId);
  }

  /**
   * Возвращает количество игроков в турнире.
   * @returns {number}
   */
  getPlayerCount() {
    return this.players.length;
  }

  /**
   * Добавляет матч в турнир.
   * @param {Game} game
   */
  addGame(game) {
    if (!(game instanceof Game)) return;

    const stage = game.stage;

    log('addGame', '🧩 Проверка стадии матча:', { stage, stages: Object.values(GameStage), valid: Object.values(GameStage).includes(stage) });
    if (!Object.values(GameStage).includes(stage)) {
      this.games.other.push(game);
      log.warn('addGame', '⚠️ Матч добавлен в "other" из-за неизвестной стадии:', stage);
      return;
    }

    if (stage === GameStage.REGULAR) {
      this.games.regular.push(game);
    } else if ([
      GameStage.R64, GameStage.R32, GameStage.R16, GameStage.R8,
      GameStage.QF, GameStage.SF, GameStage.F, GameStage.P3
    ].includes(stage)) {
      this.games.elimination.top.push(game);
    } else if ([
      GameStage.LR64, GameStage.LR32, GameStage.LR16, GameStage.LR8,
      GameStage.LQF, GameStage.LSF, GameStage.LF, GameStage.LP3
    ].includes(stage)) {
      this.games.elimination.bottom.push(game);
    }
  }

  /**
   * Возвращает список матчей регулярной стадии.
   * @returns {Array<Game>}
   */
  getRegularGames() {
    return this.games.regular || [];
  }

  /**
   * Возвращает список всех матчей плей-офф (верхняя и нижняя сетка).
   * @returns {Array<Game>}
   */
  getEliminationGames() {
    return [...(this.games.elimination.top || []), ...(this.games.elimination.bottom || [])];
  }

  /**
   * Возвращает список всех матчей турнира, включая все стадии.
   * Используется для поиска и обновления матчей.
   * @returns {Array<Game>} Массив всех матчей турнира.
   */
  getAllGames() {
    const allGames = this.games;
    const result = [];

    function recurse(value) {
      if (Array.isArray(value)) {
        result.push(...value.filter(Boolean));
      } else if (value && typeof value === 'object') {
        Object.values(value).forEach(recurse);
      }
    }

    recurse(allGames);
    return result;
  }

  /**
   * Возвращает максимальный номер раунда в регулярной стадии.
   * @returns {number}
   */
  getMaxRound() {
    return Math.max(0, ...this.getRegularRoundNumbers());
  }

  getRegularRoundNumbers() {
    const rosters = this.roundRosters && typeof this.roundRosters === 'object' && !Array.isArray(this.roundRosters)
      ? this.roundRosters : {};
    return [...new Set([
      ...this.getRegularGames().map(game => game.round),
      ...Object.keys(rosters).map(Number),
    ].filter(round => Number.isInteger(round) && round > 0))].sort((a, b) => a - b);
  }

  /** Состав именно на момент жеребьёвки, независимо от последующих замен. */
  captureRoundRoster() {
    return { participants: Object.fromEntries(this.getActiveMatchSides().map(side => [
      side.meta?.participantId ?? side.id,
      side.members.map(player => player.id),
    ])) };
  }

  getUnpairedRoundParticipants(round) {
    const participants = this.roundRosters?.[round]?.participants;
    // A missing or malformed snapshot must not prevent existing games from rendering.
    if (!participants || typeof participants !== 'object' || Array.isArray(participants)) return [];
    const assignedParticipants = new Set(this.getRegularGames()
      .filter(game => game.round === Number(round))
      .flatMap(game => [
        ...game.getSideParticipantIds('white'), ...game.getSideParticipantIds('black'),
        game.getWhiteTeamId(), game.getBlackTeamId(),
      ].filter(Boolean)));
    return Object.entries(participants)
      .filter(([participantId, playerIds]) => !assignedParticipants.has(participantId)
        && Array.isArray(playerIds) && playerIds.length > 0
        && playerIds.every(id => typeof id === 'string' && id.length > 0))
      .map(([participantId, playerIds]) => ({ participantId, playerIds: [...playerIds] }));
  }

  /**
   * Возвращает максимальный номер матча для указанной стадии.
   * @param {string|null} from - Источник матчей: 'regular', 'elimination-top', 'elimination-bottom' или null.
   * @returns {number}
   */
  getLastGameNumber(from = null) {
    let allGames;
    if (from === 'regular') {
      allGames = this.games.regular || [];
    } else if (from === 'elimination-top') {
      allGames = this.games.elimination?.top || [];
    } else if (from === 'elimination-bottom') {
      allGames = this.games.elimination?.bottom || [];
    } else {
      allGames = [
        ...(this.games.regular || []),
        ...(this.games.elimination?.top || []),
        ...(this.games.elimination?.bottom || [])
      ];
    }
    return Math.max(0, ...allGames.map(m => m.number || 0));
  }

  /**
   * Удаляет все матчи указанного раунда регулярной стадии.
   * @param {number} roundNumber
   */
  removeRegularRound(roundNumber) {
    this.games.regular = this.getRegularGames().filter(
      m => m.round !== roundNumber
    );
    const { [roundNumber]: removed, ...remaining } = this.roundRosters;
    this.roundRosters = remaining;
  }
  
  /**
   * Удаляет матчи указанной стадии плей-офф.
   * @param {string} stage - Стадия плей-офф.
   * @param {boolean} [isTop=true] - Верхняя сетка или нижняя.
   * @param {number} [round=0] - Номер раунда (0 — все раунды).
   */
  removeEliminationStage(stage, isTop = true, round = 0) {
    EliminationService.removeStage(this, stage, isTop, round);
  }

  /**
   * Генерирует следующий раунд по швейцарской системе.
   * Матчи только создаются в памяти и возвращаются наружу;
   * добавление в турнир выполняется после успешного сохранения в БД.
   * @returns {Promise<Array<Game>>}
   */
  async generateSwissRound() {
    const { default: RegularRoundService } = await import('@/services/RegularRoundService');
    return RegularRoundService.generateSwissRound(this);
  }

  /**
   * Генерирует следующий круг в круговой системе.
   * Матчи только создаются в памяти и возвращаются наружу;
   * добавление в турнир выполняется после успешного сохранения в БД.
   * @returns {Promise<Array<Game>>}
   */
  async generateRoundRobinCycle() {
    const { default: RegularRoundService } = await import('@/services/RegularRoundService');
    return RegularRoundService.generateRoundRobinCycle(this);
  }

  /**
   * Запускает плей-офф в данных турнира.
   * Создаёт стартовые игры, но не сохраняет их в БД.
   * @param {Array<Player|Team|MatchSide|Object>} [orderedParticipants=[]]
   * @returns {Promise<Game[]>}
   */
  async startElimination(orderedParticipants = []) {
    return EliminationService.start(this, orderedParticipants);
  }

  /**
   * Создаёт раунд плей-офф из уже подготовленного списка участников.
   * Источник участников может быть таблицей первого раунда или победителями предыдущей стадии.
   * @param {Array<Player|Team|MatchSide|Object>} participants
   * @param {boolean} [isTop=true] - Верхняя сетка.
   * @param {boolean} [isLoos=false] - Утешительная стадия.
   * @param {number} [level=1] - Уровень стадии/серии.
   * @returns {Promise<Game[]>}
   */
  async createElimination(participants, isTop = true, isLoos = false, level = 1) {
    return EliminationService.create(this, participants, isTop, isLoos, level);
  }

  /**
   * Создаёт Game[] раунда плей-офф из уже подготовленных пар.
   * Пары могут описывать индивидуальные встречи, fixed-команды или temporary random-команды.
   * @param {Array<Object>} pairs
   * @param {Object} [options={}]
   * @param {boolean} [options.isTop=true] - Верхняя сетка.
   * @param {boolean} [options.isLoos=false] - Утешительная стадия.
   * @param {number} [options.level=1] - Уровень стадии/серии.
   * @param {string|null} [options.stage=null] - Явно заданная стадия для дополнительных партий.
   * @param {number|null} [options.stageNumber=null] - Явно заданный номер стадии.
   * @returns {Game[]}
   */
  createEliminationGames(pairs, {
    isTop = true,
    isLoos = false,
    level = 1,
    stage = null,
    stageNumber = null,
  } = {}) {
    return EliminationService.createGames(this, pairs, { isTop, isLoos, level, stage, stageNumber });
  }

  /**
   * Вычисляет текущие турнирные таблицы (статистику игроков).
   * @param {number} [untilRound=Infinity] - До какого раунда считать.
   * @returns {Array<Object>}
   */
  async calculateStandings(untilRound = Infinity) {
    const { default: StandingsService } = await import('@/services/StandingsService');
    return StandingsService.calculate(this, untilRound);
  }

  /**
   * Возвращает список матчей указанного игрока.
   * @param {string} playerId - ID игрока.
   * @param {string|null} from - Источник матчей: 'regular', 'elimination-top', 'elimination-bottom' или null.
   * @returns {Array<Game>}
   */
  getPlayerGames(playerId, from = null) {
    let allGames;
    if (from === 'regular') {
      allGames = this.games.regular || [];
    } else if (from === 'elimination-top') {
      allGames = this.games.elimination?.top || [];
    } else if (from === 'elimination-bottom') {
      allGames = this.games.elimination?.bottom || [];
    } else {
      allGames = [
        ...(this.games.regular || []),
        ...(this.games.elimination?.top || []),
        ...(this.games.elimination?.bottom || [])
      ];
    }

    return allGames
      .filter(m => m?.getSingleWhitePlayerId?.() === playerId || m?.getSingleBlackPlayerId?.() === playerId)
      .sort((a, b) => (a.number || 0) - (b.number || 0));
  }

  /**
   * Возвращает партии индивидуального участника.
   * @param {string} participantId
   * @param {string|null} [from=null]
   * @returns {Game[]}
   */
  getIndividualParticipantGames(participantId, from = null) {
    let allGames;
    if (from === 'regular') {
      allGames = this.games.regular || [];
    } else if (from === 'elimination') {
      allGames = this.getEliminationGames();
    } else if (from === 'elimination-top') {
      allGames = this.games.elimination?.top || [];
    } else if (from === 'elimination-bottom') {
      allGames = this.games.elimination?.bottom || [];
    } else {
      allGames = [
        ...(this.games.regular || []),
        ...(this.games.elimination?.top || []),
        ...(this.games.elimination?.bottom || [])
      ];
    }

    return allGames
      .filter((game) => {
        return game?.sideHasParticipant?.('white', participantId)
          || game?.sideHasParticipant?.('black', participantId);
      })
      .sort((a, b) => (a.number || 0) - (b.number || 0));
  }

  /**
   * Возвращает агрегированные командные матчи.
   * Если teamId передан, возвращает только матчи указанной команды.
   * @param {string|null} [teamId=null]
   * @param {string|null} [from=null]
   * @returns {import('@/models/TeamMatch').default[]}
   */
  getTeamGames(teamId = null, from = null) {
    let sourceGames;
    if (from === 'regular') {
      sourceGames = this.games.regular || [];
    } else if (from === 'elimination') {
      sourceGames = this.getEliminationGames();
    } else if (from === 'elimination-top') {
      sourceGames = this.games.elimination?.top || [];
    } else if (from === 'elimination-bottom') {
      sourceGames = this.games.elimination?.bottom || [];
    } else {
      sourceGames = [
        ...(this.games.regular || []),
        ...(this.games.elimination?.top || []),
        ...(this.games.elimination?.bottom || []),
      ];
    }

    const teamMatches = this.getTeamMatches(sourceGames);
    if (!teamId) {
      return teamMatches;
    }

    return teamMatches.filter((teamMatch) =>
      teamMatch?.side1?.id === teamId || teamMatch?.side2?.id === teamId
    );
  }

  /**
   * Возвращает игры или командные матчи участника текущего формата.
   * @param {string} participantId
   * @param {string|null} [from=null]
   * @returns {Array<Game|import('@/models/TeamMatch').default>}
   */
  getParticipantGames(participantId, from = null) {
    if (this.isTeamTournament()) {
      return this.getTeamGames(participantId, from);
    }

    return this.getIndividualParticipantGames(participantId, from);
  }

  /**
   * Возвращает серии матчей плей-офф для указанной стадии или всех стадий.
   * @param {string|null} stage
   * @returns {Promise<Array<Series>>}
   */
  async getEliminationSeries(stage = null) {
    return EliminationService.getSeries(this, stage);
  }

  /**
   * Обновляет данные о матче в турнире.
   * Производит поиск матча по всем стадиям и обновляет его данные, если найден.
   * Проверяет корректность игроков и важных полей.
   * @param {Object} updatedGame - Обновлённые данные матча.
   * @returns {Object} Результат обновления: { success: true } или объект ошибки.
   */
  updateGame(updatedGame) {
    // Ищем матч по всем стадиям
    const allGames = this.getAllGames();

    log('updateGame', '📦 allGames:', Object.values(allGames));

    const existing = allGames.find(m => m.id === updatedGame.id);

    if (!existing) {
      return { error: translate('serviceMessages.gameNotFound'), gameId: updatedGame?.id };
    }

    // ✅ Валидация состава игроков
    const whiteCount = Array.isArray(updatedGame.whitePlayerIds) ? updatedGame.whitePlayerIds.length : 0;
    const blackCount = Array.isArray(updatedGame.blackPlayerIds) ? updatedGame.blackPlayerIds.length : 0;
    const whiteValid = whiteCount > 0;
    const blackValid = blackCount > 0;
    const playersValid = updatedGame.isBye
      ? whiteValid !== blackValid
      : whiteValid && blackValid;

    if (!playersValid) {
      return {
        error: translate('serviceMessages.invalidGamePlayers'),
        white: updatedGame.whitePlayerIds,
        black: updatedGame.blackPlayerIds,
        isBye: updatedGame.isBye
      };
    }

    // Стадия и цикл определяют раздел хранения матча. Позиция внутри
    // сетки (level, stageNumber, bracketIndex) редактируется в форме.
    const fields = ['stage', 'cycle'];
    for (const field of fields) {
      if (existing[field] !== updatedGame[field]) {
        return {
          error: translate('serviceMessages.gameFieldMismatch', { field }),
          expected: existing[field],
          received: updatedGame[field]
        };
      }
    }

    const wasChanged = JSON.stringify(updatedGame) !== JSON.stringify(existing);
    const resultChanged = updatedGame.result !== existing.result;

    if (wasChanged) {
      updatedGame.updatedAt = new Date().toISOString();
      if (resultChanged) {
        updatedGame.resultSetAt = updatedGame.result
          ? new Date().toISOString()
          : null;
      }
    }

    Object.assign(existing, updatedGame);
    return { success: true };
  }

  /**
   * Прогресс по швейцарским турам: сколько запланировано и сколько фактически есть.
   * НЕ проверяет результаты партий.
   * @returns {{plannedCount:number, existingCount:number, rounds:number[], isComplete:boolean}}
   */
  checkSwissRoundsProgress() {
    const planned = this.getSwissSettings()?.rounds ?? 0;
    const rounds = this.getRegularRoundNumbers();
    const existing = rounds.length;
    return {
      plannedCount: planned,
      existingCount: existing,
      rounds,
      isComplete: planned > 0 ? existing >= planned : existing > 0
    };
  }

  /**
   * Проверка завершённости регулярных матчей турнира:
   * у каждого обычного матча должен быть установлен result, а bye-матч считается завершённым автоматически.
   * @returns {{totalCount:number, finishedCount:number, unfinishedCount:number, unfinished:string[]}}
   */
  checkRegularGamesFinished() {
    const allGames = this.getRegularGames();
    const games = this.system === TournamentSystem.ROUND_ROBIN
      ? allGames.filter((m) => !m?.isBye)
      : allGames;

    const isFinished = (m) => Boolean(m?.result) || Boolean(m?.isBye) || TerminationForfeit.includes(m?.termination);
    const unfinishedIds = games.filter(m => !isFinished(m)).map(m => m.id);
    const totalCount = games.length;
    const finishedCount = games.length - unfinishedIds.length;
    const isComplete = unfinishedIds.length === 0;
    return {
      totalCount,
      finishedCount,
      unfinishedCount: unfinishedIds.length,
      unfinished: unfinishedIds,
      isComplete
    };
  }

  /**
   * Проверяет, считается ли плей‑офф «завершённым» с точки зрения наличия финала.
   *
   * Логика:
   *  • Всегда требуется наличие финала верхней сетки (stage === F).
   *  • Если bracketsType === 'top-bottom', дополнительно требуется финал нижней сетки (stage === LF).
   *
   * Возвращает:
   *  • true — если выполнены требования выше;
   *  • false — иначе.
   *
   * @returns {boolean}
   */
  checkEliminationComplete() {
    return EliminationService.checkComplete(this);
  }

  /**
   * Проверка завершённости матчей плей-офф: у каждого матча плей-офф должен быть установлен result.
   * @returns {{totalCount:number, finishedCount:number, unfinishedCount:number, unfinished:string[]}}
   */
  checkEliminationGamesFinished() {
    return EliminationService.checkGamesFinished(this);
  }

  /**
   * Проверяет, определены ли победители во всех сериях плей-офф.
   * Возвращает объект с общим количеством серий, количеством завершённых, количеством незавершённых и списком незавершённых.
   * @returns {{totalCount:number, finishedCount:number, unfinishedCount:number, unfinished:Array<Object>}}
   */
  async checkEliminationHasWinners() {
    return EliminationService.checkHasWinners(this);
  }

}
